/* ==========================================================================
   Start Nutra · Home hero variant: "first light"
   --------------------------------------------------------------------------
   Timer-fill SKU switcher: one requestAnimationFrame loop drives BOTH the
   fill's scaleX and the dual-layer label's clip-path every frame, so they
   can never drift relative to each other. Progress is LINEAR (a timer reads
   linearly); the switch crossfade (image/headline/subhead) uses the brand
   curve, driven by CSS transitions with a JS-coordinated midpoint content
   swap.

   "First light" is the sole hero (the preview toggle and the other two
   variants have been removed): it starts itself on init, unconditionally.

   Reduced motion: a real branch, not a CSS override. The rAF loop never
   starts; manual selection swaps content/classes synchronously with no
   crossfade. The matching resting CSS (solid white active pill, dark label)
   lives in section-sn-home-hero-breath.css's reduced-motion block.
   ========================================================================== */

(function () {
  'use strict';

  var REDUCED = '(prefers-reduced-motion: reduce)';

  function prefersReduced() {
    return !!(window.matchMedia && window.matchMedia(REDUCED).matches);
  }

  function init() {
    var section = document.querySelector('[data-fl-hero]');
    if (!section) return;

    var reduced = prefersReduced();
    var eyebrow = section.querySelector('[data-fl-eyebrow]');
    var eyebrowLabel = section.querySelector('[data-fl-eyebrow-label]');
    var headline = section.querySelector('[data-fl-headline]');
    var subhead = section.querySelector('[data-fl-subhead]');
    var cta = section.querySelector('[data-fl-cta]');
    var switcher = section.querySelector('[data-fl-switcher]');
    var segments = Array.prototype.slice.call(section.querySelectorAll('[data-fl-segment]'));
    var images = Array.prototype.slice.call(section.querySelectorAll('[data-fl-image]'));
    var scenes = Array.prototype.slice.call(section.querySelectorAll('[data-fl-scene]'));

    var N = segments.length;
    if (!N) return;

    var durationMs = (parseFloat(section.getAttribute('data-fl-duration')) || 6) * 1000;
    // Auto-switch toggle (theme editor setting): "false" is the only value
    // that turns it off: missing/anything else defaults to on, matching
    // the schema checkbox's own default: true.
    var autoplayEnabled = section.getAttribute('data-fl-autoplay') !== 'false';
    // Progress-fill toggle (theme editor setting), same "false is the only
    // off value" rule. Off: setProgress() below never writes, so no inline
    // transform/clip-path ever exists on a segment and the CSS keyed off
    // [data-fl-progress-fill="false"] + .is-active alone draws the
    // highlighted tab. The rAF loop still runs: it is the auto-advance
    // clock, not just the fill painter.
    var showFill = section.getAttribute('data-fl-progress-fill') !== 'false';

    var activeIndex = 0;
    var hovering = false;        // pointer over switcher
    var focused = false;         // keyboard focus within switcher
    var isPausedByUser = false;  // click-to-toggle on the hero container: persists until toggled again
    var pageVisible = !document.hidden;
    var switching = false;       // guards the crossfade sequence
    var started = false;

    // Desktop/mobile split, shared by the container click-to-toggle below
    // and the mobile swipe gesture guard further down: one query, one
    // source of truth, so neither can disagree about which side of 1024px
    // it's on.
    var desktopQuery = window.matchMedia ? window.matchMedia('(min-width: 1024px)') : null;

    var cycleStart = 0;
    var elapsedBeforePause = 0;
    var pausedAt = 0;
    var rafId = null;

    /* ---- Segment fill + dual-layer label (one loop drives both) ---- */
    function fillEl(seg) { return seg.querySelector('[data-fl-fill]'); }
    function topLabelEl(seg) { return seg.querySelector('[data-fl-label-top]'); }

    function setProgress(seg, progress) {
      if (!showFill) return;
      var f = fillEl(seg);
      var t = topLabelEl(seg);
      if (f) f.style.transform = 'scaleX(' + progress + ')';
      if (t) t.style.clipPath = 'inset(0 ' + ((1 - progress) * 100) + '% 0 0)';
    }

    function resetSegmentVisual(seg) {
      setProgress(seg, 0);
    }

    function canRun() {
      // pausedAt !== 0 freezes tick() entirely (no progress written this
      // frame), which is what actually pauses the visible fill, without
      // this check the rAF loop would keep computing elapsed time from the
      // untouched cycleStart and the fill would keep sweeping regardless of
      // hover/focus/visibility state.
      return autoplayEnabled && !reduced && pageVisible && pausedAt === 0 && !switching && N > 1;
    }

    function tick(now) {
      rafId = window.requestAnimationFrame(tick);
      if (!canRun()) return;

      var elapsed = elapsedBeforePause + (now - cycleStart);
      var progress = elapsed / durationMs;
      if (progress >= 1) {
        advance();
        return;
      }
      setProgress(segments[activeIndex], progress);
    }

    function startLoop() {
      if (rafId === null) rafId = window.requestAnimationFrame(tick);
    }

    function resetCycle() {
      cycleStart = window.performance.now();
      elapsedBeforePause = 0;
    }

    /* ---- Pause / resume (hover, focus, tab hidden): resumes where it left off ---- */
    function pauseTimer() {
      if (pausedAt === 0) pausedAt = window.performance.now();
    }
    function resumeTimer() {
      if (pausedAt !== 0) {
        elapsedBeforePause += pausedAt - cycleStart;
        cycleStart = window.performance.now();
        pausedAt = 0;
      }
    }

    /* ---- Roving tabindex + aria-checked ---- */
    function setSegmentState(i) {
      segments.forEach(function (seg, si) {
        var on = si === i;
        seg.classList.toggle('is-active', on);
        seg.setAttribute('aria-checked', on ? 'true' : 'false');
        seg.setAttribute('tabindex', on ? '0' : '-1');
        if (!on) {
          resetSegmentVisual(seg);
        } else if (!autoplayEnabled) {
          // No rAF loop runs to sweep this when auto-switch is off, so the
          // active capsule would otherwise sit permanently empty: fill it
          // immediately as the "this one's selected" cue instead.
          setProgress(seg, 1);
        }
      });
    }

    /* ---- Image crossfade (300ms out / 600ms in via CSS; .is-pre-enter
       primes the entering layer at translateY(6px) for exactly one frame
       so the 600ms transition animates the rise rather than snapping) ---- */
    function switchImage(i) {
      images.forEach(function (img, ii) {
        if (ii === i) {
          img.classList.remove('is-active');
          img.classList.add('is-pre-enter');
          img.offsetHeight; // force reflow so the pre-enter state actually paints
          window.requestAnimationFrame(function () {
            img.classList.remove('is-pre-enter');
            img.classList.add('is-active');
          });
        } else {
          img.classList.remove('is-active', 'is-pre-enter');
        }
      });
    }

    /* ---- Scene crossfade (optional per-SKU background photo). Sparse list,
       only blocks with a scene image render a [data-fl-scene] element, so
       (unlike images/switchImage) position in the array does not equal
       block index; matched by the attribute's own value instead. Called
       from the same sites as switchImage(), same index, no new timing. ---- */
    function switchScene(i) {
      scenes.forEach(function (scene) {
        var si = parseInt(scene.getAttribute('data-fl-scene'), 10);
        scene.classList.toggle('is-active', si === i);
      });
    }

    /* ---- Full switch sequence (auto-advance or manual) ---- */
    function switchTo(i) {
      if (i === activeIndex && started) { resetCycle(); return; }
      if (switching) return;

      var seg = segments[i];
      if (!seg) return;

      var newEyebrow = seg.getAttribute('aria-label') || '';
      var newHeadline = seg.getAttribute('data-fl-headline-text') || '';
      var newSubhead = seg.getAttribute('data-fl-subhead-text') || '';
      var newUrl = seg.getAttribute('data-fl-url') || '';
      var newCtaText = seg.getAttribute('data-fl-cta-text') || '';

      if (reduced) {
        // Instant, synchronous swap, no crossfade, no timers.
        // Eyebrow text lives in its own child span (see markup) so this
        // never touches the sibling .sn-tm mark.
        if (eyebrowLabel) eyebrowLabel.textContent = newEyebrow;
        if (headline) headline.textContent = newHeadline;
        if (subhead) subhead.textContent = newSubhead;
        if (cta && newUrl) cta.setAttribute('href', newUrl);
        if (cta && newCtaText) cta.textContent = newCtaText;
        switchImage(i);
        switchScene(i);
        setSegmentState(i);
        activeIndex = i;
        started = true;
        return;
      }

      switching = true;
      if (eyebrow) eyebrow.classList.add('is-leaving');
      if (headline) headline.classList.add('is-leaving');
      if (subhead) subhead.classList.add('is-leaving');

      window.setTimeout(function () {
        // Midpoint: old content is fully faded, swap everything together.
        // eyebrowLabel is a dedicated child span, never the eyebrow element
        // itself, so the sibling .sn-tm mark is never touched/removed.
        if (eyebrowLabel) eyebrowLabel.textContent = newEyebrow;
        if (headline) headline.textContent = newHeadline;
        if (subhead) subhead.textContent = newSubhead;
        if (cta && newUrl) cta.setAttribute('href', newUrl);
        if (cta && newCtaText) cta.textContent = newCtaText;
        switchImage(i);
        switchScene(i);
        setSegmentState(i);
        activeIndex = i;
        started = true;

        if (eyebrow) eyebrow.classList.remove('is-leaving');
        if (headline) headline.classList.remove('is-leaving');
        if (subhead) subhead.classList.remove('is-leaving');

        resetCycle();
        switching = false;
      }, 250);
    }

    function advance() {
      switchTo((activeIndex + 1) % N);
    }

    /* ---- Manual selection (click) ---- */
    segments.forEach(function (seg, i) {
      seg.addEventListener('click', function () {
        switchTo(i);
        seg.focus();
      });
    });

    /* ---- Radiogroup keyboard: arrows both move focus AND change selection ---- */
    if (switcher) {
      switcher.addEventListener('keydown', function (e) {
        if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].indexOf(e.key) === -1) return;
        e.preventDefault();
        var dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
        var next = (activeIndex + dir + N) % N;
        switchTo(next);
        if (segments[next]) segments[next].focus();
      });
    }

    /* ---- Mobile card expand/collapse state (toggled by the chevron button
       and by the unified gesture handler below). Collapsed is the only
       state on load; never persisted. ---- */
    var card = section.querySelector('.sn-hero-fl__copy');
    var toggle = section.querySelector('.sn-hero-card__toggle');
    var toggleLabel = toggle ? toggle.querySelector('.visually-hidden') : null;
    var expanded = false;

    function setExpanded(next) {
      if (!card || !toggle) return;
      if (next === expanded) return;
      expanded = next;
      card.classList.toggle('is-expanded', expanded);
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (toggleLabel) toggleLabel.textContent = expanded ? 'Hide details' : 'Show details';
    }

    /* Chevron: tap/click/keyboard (Enter/Space activate a <button> natively,
       dispatching this same click, no separate keydown handler needed). */
    if (toggle) {
      toggle.addEventListener('click', function () {
        setExpanded(!expanded);
      });
    }

    /* ---- Mobile: ONE unified gesture pass across the whole hero panel,
       image, card and switcher all included, deciding swipe (SKU change)
       vs tap (card toggle) vs neither (vertical page scroll, or an
       inconclusive partial drag). This REPLACES the previous two separate
       systems (an image-only swipe zone, and a card-only vertical-drag
       zone) with one shared decision per touch sequence, so a swipe that
       happens to start over the card can never also be misread as a tap,
       and vice versa: there is no way for two independent listeners to
       disagree because there is only one.

       Direction-locked exactly as before: nothing is decided until
       movement clears LOCK_PX (10px) in either axis, then whichever axis
       moved further wins. Only once horizontal is confirmed do we
       preventDefault() (on a {passive:false} listener, required for it to
       have any effect): a vertical lock calls preventDefault() on
       NOTHING, so page scroll is completely normal everywhere in the hero,
       including when a gesture starts on the card or switcher.

       On release:
         - horizontal-locked AND |dx| >= SWIPE_PX (45px) -> SKU swipe:
           switchTo() directly, the same path pills/arrow-keys/click use.
           Never also toggles the card, regardless of where it started.
         - never left LOCK_PX in EITHER axis (a clean tap, not a drag) ->
           if the tap landed on an interactive descendant (a switcher pill,
           the Shop/science links, the chevron button: closest('a,
           button')) this does NOTHING; preventDefault was never called for
           this gesture, so the browser's normal click fires on that
           element and its OWN existing listener handles it, completely
           unaffected. Otherwise (plain card body, or the open image area)
           toggle the card.
         - anything else (a vertical scroll, or a horizontal drag that
           didn't reach 45px) -> no-op; the gesture is simply released.

       Guarded to mobile widths only via matchMedia, unlike the previous
       image-only zone, this listens on .sn-hero-fl__panel, which also
       contains the switcher/CTA that must keep receiving ordinary desktop
       MOUSE clicks; touch events never fire from a mouse regardless, but a
       desktop-sized touchscreen is a real (if rare) device class, and the
       previous implementation relied on .sn-hero-scenes' own
       pointer-events:none at desktop widths for that same guarantee, this
       explicit check preserves it now that the listener covers a wider,
       always-interactive element. */
    var panel = section.querySelector('.sn-hero-fl__panel');
    if (panel) {
      var LOCK_PX = 10;
      var SWIPE_PX = 45;
      var gestureX = 0;
      var gestureY = 0;
      var gestureTarget = null;
      var gestureTracking = false;
      var gestureLockedAxis = null; // 'x' | 'y' | null until decided

      panel.addEventListener('touchstart', function (e) {
        if (desktopQuery && desktopQuery.matches) return;
        if (e.touches.length !== 1) return;
        gestureX = e.touches[0].clientX;
        gestureY = e.touches[0].clientY;
        gestureTarget = e.target;
        gestureTracking = true;
        gestureLockedAxis = null;
      }, { passive: true });

      panel.addEventListener('touchmove', function (e) {
        if (!gestureTracking || e.touches.length !== 1) return;
        var dx = e.touches[0].clientX - gestureX;
        var dy = e.touches[0].clientY - gestureY;
        if (gestureLockedAxis === null) {
          if (Math.abs(dx) < LOCK_PX && Math.abs(dy) < LOCK_PX) return;
          gestureLockedAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }
        if (gestureLockedAxis === 'x') e.preventDefault(); // suppress native scroll only once horizontal is confirmed
      }, { passive: false });

      panel.addEventListener('touchend', function (e) {
        if (!gestureTracking) return;
        gestureTracking = false;

        var endX = e.changedTouches.length ? e.changedTouches[0].clientX : gestureX;
        var endY = e.changedTouches.length ? e.changedTouches[0].clientY : gestureY;
        var dx = endX - gestureX;
        var dy = endY - gestureY;

        if (gestureLockedAxis === 'x' && Math.abs(dx) >= SWIPE_PX) {
          var dir = dx < 0 ? 1 : -1; // swipe left -> next SKU, swipe right -> previous
          switchTo((activeIndex + dir + N) % N);
          return;
        }

        if (gestureLockedAxis === null) {
          // Never left LOCK_PX in either axis: a clean tap.
          if (gestureTarget && gestureTarget.closest && gestureTarget.closest('a, button')) return;
          setExpanded(!expanded);
        }
        // else: a vertical scroll, or a horizontal drag under SWIPE_PX, no-op.
      }, { passive: true });

      panel.addEventListener('touchcancel', function () {
        gestureTracking = false;
        gestureLockedAxis = null;
      }, { passive: true });
    }

    /* ---- Pause on hover / keyboard focus / user toggle / hidden tab ----
       All four conditions share one pausedAt sentinel, so they must be
       reconciled together: resume only when hovering, focused,
       isPausedByUser, AND hidden have ALL cleared: e.g. the tab regaining
       visibility while the pointer is still over the switcher must NOT
       resume the timer, and the pointer leaving while a segment still
       holds keyboard focus must NOT resume it either. isPausedByUser is the
       odd one out: unlike the other three it doesn't clear itself on an
       input event (mouseleave/blur/visibilitychange), only another click
       on the container flips it back, which is what makes it a persistent
       toggle rather than a transient "currently hovering" flag.

       Fix 4: "focused" must mean KEYBOARD focus specifically, not any
       focus. Clicking/tapping a <button> focuses it natively in Chrome/
       Firefox/Edge, and a click is never followed by a natural blur:
       treating that as "focused" paused the cycle permanently after any
       manual pointer selection. :focus-visible is the browser's own input-
       modality signal (true for keyboard-driven focus, false for pointer-
       driven focus/programmatic .focus() following a click), so checking it
       here is what actually distinguishes the two cases the brief asks for. */
    function syncPause() {
      if (hovering || focused || isPausedByUser || !pageVisible) pauseTimer();
      else resumeTimer();
    }
    if (switcher) {
      switcher.addEventListener('mouseenter', function () { hovering = true; syncPause(); });
      switcher.addEventListener('mouseleave', function () { hovering = false; syncPause(); });
      switcher.addEventListener('focusin', function (e) {
        var el = e.target;
        if (el && el.matches && el.matches(':focus-visible')) {
          focused = true;
          syncPause();
        }
      });
      switcher.addEventListener('focusout', function () {
        if (!switcher.contains(document.activeElement)) { focused = false; syncPause(); }
      });
    }

    /* ---- Click-to-toggle autoplay pause ----
       Replaces the old press-and-hold: one click anywhere in the hero
       pauses auto-advance, the next click resumes it, and, unlike
       hover/focus pause, the paused state persists until toggled again.
       Desktop only (see desktopQuery above); ignored on the switcher, any
       <a>, any <button>, and anything inside the glass copy card, via
       closest().

       Routed through the same pausedAt/syncPause() machinery as
       hover/focus/visibility pause, so nothing new is needed to freeze or
       resume the timer-fill: it's drawn every frame from inline styles in
       tick() (not a CSS animation), and tick() already no-ops while
       pausedAt is set, so the fill simply stops being written to on pause
       and continues from its last-written value on resume.

       Switcher pills keep changing the slide via their own listener above,
       untouched: switchTo()/resetCycle() never touch pausedAt, so a pill
       click while paused resets the timer but leaves the pause in place. */
    var pauseStatus = section.querySelector('[data-fl-pause-status]');

    function isInteractiveTarget(target) {
      return !!(target && target.closest &&
        target.closest('[data-fl-switcher], a, button, .sn-hero-fl__copy'));
    }

    function announcePauseState() {
      if (pauseStatus) {
        pauseStatus.textContent = isPausedByUser ? 'Carousel paused' : 'Carousel resumed';
      }
    }

    function toggleUserPause() {
      isPausedByUser = !isPausedByUser;
      syncPause();
      announcePauseState();
    }

    section.addEventListener('click', function (e) {
      if (reduced) return;
      if (!autoplayEnabled) return; // nothing auto-advances to pause/resume
      if (!(desktopQuery && desktopQuery.matches)) return;
      if (isInteractiveTarget(e.target)) return;
      toggleUserPause();
    });

    document.addEventListener('visibilitychange', function () {
      pageVisible = !document.hidden;
      syncPause();
    });

    /* ---- Start the hero. First light is the only hero (the preview toggle
       and the other two variants are gone): it starts itself, once, on
       init. ---- */
    function activate() {
      // Index 0 is already the active segment, image and scene in the
      // server-rendered markup, so nothing is switched here: calling
      // switchImage(0)/switchScene(0) would strip .is-active from what is
      // already painted and fade it back in. State and the cycle only. The
      // load-in entrance is CSS (section-sn-home-hero-breath.css), not JS.
      setSegmentState(0);
      activeIndex = 0;
      started = true;
      resetCycle();
      if (!reduced && autoplayEnabled) startLoop();
    }

    activate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
